import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import { UsernameAuthForm } from '../../_NEXT_components/UsernameAuthForm';
import { ContentLayout } from '../../_NEXT_components/ContentLayout';
import { Button, EButtonIntent } from '../../_NEXT_components/Button';
import { EActiveFlow } from '../../types/flow';
import { isEmail, isValidPhone } from '../../utils/validation';
import { EActiveView } from '../../types/view';
import { trackChangeLoginFlowTo, trackForgotPasswordClick, trackLoginResumeButtonClick, trackShowTwoStepPassword } from '../../utils/tracking';
import { RootContext } from '../RootContainer';
export function UsernameAuthFormContainer(_ref) {
  var isSubmitting = _ref.isSubmitting,
      activeFlow = _ref.activeFlow,
      submitButtonText = _ref.submitButtonText,
      login = _ref.login,
      loginError = _ref.loginError,
      isLoginInvalid = _ref.isLoginInvalid,
      hasAsyncLoginError = _ref.hasAsyncLoginError,
      password = _ref.password,
      passwordError = _ref.passwordError,
      isPasswordInvalid = _ref.isPasswordInvalid,
      hasAsyncPasswordError = _ref.hasAsyncPasswordError,
      hasAsyncPhoneError = _ref.hasAsyncPhoneError,
      phoneError = _ref.phoneError,
      changeLogin = _ref.changeLogin,
      changePassword = _ref.changePassword,
      authenticate = _ref.authenticate,
      register = _ref.register,
      checkEmail = _ref.checkEmail,
      changeView = _ref.changeView,
      changeFlow = _ref.changeFlow,
      resetToPhoneAuth = _ref.resetToPhoneAuth,
      withSubscriptionAgreement = _ref.withSubscriptionAgreement;

  // FORM
  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      changed = _React$useState2[0],
      setChanged = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      touched = _React$useState4[0],
      setTouched = _React$useState4[1];

  var _React$useContext = React.useContext(RootContext),
      autoFocusDisabled = _React$useContext.autoFocusDisabled; // LOGIN


  var loginRef = React.useRef(null);

  var handleLoginChange = function handleLoginChange(e) {
    var value = e.currentTarget.value;
    changeLogin(value);
    setChanged(true);
  }; // PASSWORD


  var passwordRef = React.useRef(null);

  var handlePasswordChange = function handlePasswordChange(e) {
    var value = e.currentTarget.value;
    changePassword(value);
    setChanged(true);
  };

  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      passwordShown = _React$useState6[0],
      setPasswordShown = _React$useState6[1];

  var togglePasswordVisibility = React.useCallback(function () {
    return setPasswordShown(!passwordShown);
  }, [passwordShown]);
  var handleResetLinkClick = React.useCallback(function (e) {
    e.preventDefault();
    trackForgotPasswordClick();
    changeView(EActiveView.Restore, isEmail(login) ? {
      email: login
    } : undefined);
  }, [changeView, login]); // PHONE

  var phoneRef = React.useRef(null);

  var _React$useState7 = React.useState(''),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      phone = _React$useState8[0],
      setPhone = _React$useState8[1];

  var isPhoneInvalid = React.useMemo(function () {
    return phone !== '' && !isValidPhone(phone);
  }, [phone]);

  var handlePhoneChange = function handlePhoneChange(e) {
    var value = e.currentTarget.value;
    setPhone(value);
    setChanged(true);
  }; // SUBSCRIPTION


  var _React$useState9 = React.useState(true),
      _React$useState10 = _slicedToArray(_React$useState9, 2),
      subscription = _React$useState10[0],
      setSubscription = _React$useState10[1];

  var handleChangeSubscription = React.useCallback(function (e) {
    var checked = e.currentTarget.checked;
    setSubscription(checked);
  }, []); // PRO USER

  var _React$useState11 = React.useState(false),
      _React$useState12 = _slicedToArray(_React$useState11, 2),
      isProfessionalUser = _React$useState12[0],
      setProfessionalUser = _React$useState12[1];

  var handleProfessionalUserChange = React.useCallback(function (e) {
    var checked = e.currentTarget.checked;
    setProfessionalUser(checked);
  }, []); // SUBMITION

  var handleFocusInvalid = React.useCallback(function () {
    if (isLoginInvalid && loginRef.current) {
      loginRef.current.focus();
      return;
    }

    if (isPasswordInvalid && passwordRef.current) {
      passwordRef.current.focus();
      return;
    }

    if (isPhoneInvalid && phoneRef.current) {
      phoneRef.current.focus();
      return;
    }
  }, [isPasswordInvalid, isPhoneInvalid, isLoginInvalid]);

  var handleSubmit = function handleSubmit() {
    setTouched(true);

    switch (activeFlow) {
      case EActiveFlow.Registration:
        if (isLoginInvalid || isPasswordInvalid || isPhoneInvalid) {
          handleFocusInvalid();
          return;
        }

        register({
          email: login,
          password: password,
          phone: phone,
          wantsSubscription: subscription,
          isProfessional: isProfessionalUser
        });
        break;

      case EActiveFlow.Authentication:
        if (isLoginInvalid || isPasswordInvalid) {
          handleFocusInvalid();
          return;
        }

        authenticate({});
        break;

      default:
        if (isLoginInvalid) {
          handleFocusInvalid();
          return;
        }

        trackLoginResumeButtonClick();

        if (isEmail(login)) {
          checkEmail();
        } else {
          trackShowTwoStepPassword('id');
          changeFlow(EActiveFlow.Authentication);
        }

    }

    setChanged(false);
  };

  var switchView = function switchView() {
    trackChangeLoginFlowTo('phone');
    resetToPhoneAuth();
  };

  React.useEffect(function () {
    setPasswordShown(false);
    setPhone('');
    setSubscription(true);

    if ((activeFlow === EActiveFlow.Registration || activeFlow === EActiveFlow.Authentication) && passwordRef.current) {
      setTouched(false);
      setChanged(false);
      passwordRef.current.focus();
    }
  }, [activeFlow]);
  return /*#__PURE__*/React.createElement(ContentLayout, null, /*#__PURE__*/React.createElement(UsernameAuthForm, {
    activeFlow: activeFlow,
    handleSubmit: handleSubmit,
    isSubmitting: isSubmitting,
    submitButtonText: submitButtonText,
    autoFocusDisabled: autoFocusDisabled || false // LOGIN
    ,
    loginRef: loginRef,
    login: login,
    onLoginChange: handleLoginChange,
    loginError: !changed && loginError || undefined,
    isLoginInvalid: touched && (isLoginInvalid || !changed && hasAsyncLoginError) // PASSWORD
    ,
    passwordRef: passwordRef,
    password: password,
    passwordShown: passwordShown,
    isPasswordInvalid: touched && (isPasswordInvalid || !changed && hasAsyncPasswordError),
    passwordError: passwordError,
    togglePasswordVisibility: togglePasswordVisibility,
    onPasswordChange: handlePasswordChange,
    onResetLinkClick: handleResetLinkClick // PHONE
    ,
    phoneRef: phoneRef,
    phone: phone,
    onPhoneChange: handlePhoneChange,
    isPhoneInvalid: isPhoneInvalid || !changed && hasAsyncPhoneError,
    phoneError: phoneError // SUBSCRIPTION
    ,
    subscriptionChecked: subscription,
    onSubscriptionChange: handleChangeSubscription,
    withSubscriptionAgreement: withSubscriptionAgreement // PRO USER
    ,
    isProfessionalUser: isProfessionalUser,
    onPorfessionalUserChange: handleProfessionalUserChange
  }), activeFlow === null && /*#__PURE__*/React.createElement(Button, {
    dataMark: "SwitchToPhoneAuth",
    fill: true,
    intent: EButtonIntent.Secondary,
    onClick: switchView
  }, "\u0412\u043E\u0439\u0442\u0438 \u043F\u043E \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0443"));
}